import { useEffect, useMemo, useRef, type ReactNode, type RefObject } from "react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { LibraryAnimaticNode } from "@/components/library/library-animatic-node"
import { LibraryBreadcrumbs } from "@/components/library/library-breadcrumbs"
import { LibraryCanvasNode } from "@/components/library/library-canvas-node"
import { LibraryEntityFolder } from "@/components/library/library-entity-folder"
import { LibraryFlowNode } from "@/components/library/library-flow-node"
import { type LibraryAsset } from "@/components/library/library-item"
import {
  LibraryPickProvider,
  LibrarySortProvider,
  type LibraryPickContextValue,
} from "@/components/library/library-items"
import { LibraryKindFilter, LibraryKindFilterProvider } from "@/components/library/library-kind-filter"
import { LibraryNodeEmpty } from "@/components/library/library-node-state"
import { LibrarySearchField } from "@/components/library/library-search-field"
import { LibrarySearchResults } from "@/components/library/library-search-results"
import { LibraryShotFolder } from "@/components/library/library-shot-folder"
import { LibrarySketchesNode } from "@/components/library/library-sketches-node"
import { LibraryUploadsNode } from "@/components/library/library-uploads-node"
import { LibraryTree } from "@/components/library/library-tree"
import { LibraryViewToggle } from "@/components/library/library-view-toggle"
import { useLibrarySearch } from "@/hooks/use-library-search"
import { useTranslation } from "@/hooks/use-translation"
import type { useLibraryBrowser } from "@/hooks/use-library-browser"
import { formatMs } from "@/lib/format-ms"
import type {
  LibraryCanvasRow,
  LibraryEntitySections,
  LibraryFlowRow,
  LibrarySketchesSummary,
  LibraryUploadsSummary,
  LibraryTree as LibraryTreeData,
} from "@/lib/library-api"
import { cn } from "@/lib/utils"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"

export type LibraryBrowserState = ReturnType<typeof useLibraryBrowser>

type Props = {
  browser: LibraryBrowserState
  projectId: number
  tree: LibraryTreeData
  canvases: LibraryCanvasRow[]
  flows: LibraryFlowRow[]
  entities: LibraryEntitySections
  sketches: LibrarySketchesSummary
  uploads: LibraryUploadsSummary
  /** Секция «Загрузки» умеет мутировать — страница передаёт сюда права и триггер обновления. */
  uploadsOptions?: {
    reloadToken?: number
    /** Растёт, когда страница просит показать «Загрузки»: гасит поиск и открывает секцию. */
    revealToken?: number
    canEdit?: boolean
    onCountChange?: () => void
  }
  activeEpisodeId: number | null
  frameFormat: string
  frameRate?: number
  /** Чем окружено одно и то же шасси: page рисует поверхность сам, dialog берёт её у модалки. */
  variant: "page" | "dialog"
  rootRef: RefObject<HTMLDivElement | null>
  contentRef: RefObject<HTMLDivElement | null>
  /** Слева от крошек: тумблер дерева в модалке. */
  toolbarStart?: ReactNode
  /** Сразу за путём: действие над открытой папкой — «Открыть» у папки шота. */
  crumbsEnd?: ReactNode
  toolbarEnd?: ReactNode
  /** Непусто — включён режим выбора: плитки выделяются, а не открываются. */
  pick?: LibraryPickContextValue | null
  /** Модалка: колонка дерева скрыта, содержимое занимает всю ширину. */
  treeHidden?: boolean
}

/**
 * Браузер Библиотеки: дерево слева, содержимое папки справа, просмотрщик поверх.
 * Одно и то же наполнение на двух шасси — страница раздела и модалка выбора кадра;
 * различается только обёртка и режим выбора, приходящий контекстом.
 */
export function LibraryBrowser({
  browser,
  projectId,
  tree,
  canvases,
  flows,
  entities,
  sketches,
  uploads,
  uploadsOptions,
  activeEpisodeId,
  frameFormat,
  frameRate,
  variant,
  rootRef,
  contentRef,
  toolbarStart,
  crumbsEnd,
  toolbarEnd,
  pick = null,
  treeHidden = false,
}: Props) {
  const { t, locale } = useTranslation(["workspace", "common"])
  const { selection, select, viewMode, setViewMode } = browser
  const search = useLibrarySearch(projectId)

  // Показать «Загрузки» по просьбе страницы (закончилась загрузка файлов). Гасить поиск
  // обязано именно шасси: его состояние живёт здесь и замещает содержимое папки целиком,
  // так что одного select() снаружи было бы мало — загруженное осталось бы не видно.
  const revealToken = uploadsOptions?.revealToken ?? 0
  const revealedRef = useRef(revealToken)
  // Колбэки держим в ref, а не в deps: search.clear пересоздаётся каждый рендер, и эффект
  // с ним в зависимостях крутился бы вхолостую на каждую отрисовку браузера.
  const revealActionsRef = useRef({ clear: search.clear, select })
  revealActionsRef.current = { clear: search.clear, select }
  useEffect(() => {
    if (revealToken === revealedRef.current) return
    revealedRef.current = revealToken
    revealActionsRef.current.clear()
    revealActionsRef.current.select({ section: "uploads" })
  }, [revealToken])

  const contentCursorKey = browser.focusZone === "content" && browser.keyboardCursor ? browser.focusedKey : null
  const nodeProps = {
    mode: viewMode,
    focusedKey: contentCursorKey,
    onFocusItem: browser.setFocusedKey,
    onOpenItem: browser.handleOpenItem,
    onItemsChange: browser.handleItemsChange,
  }

  let content: ReactNode
  if (search.active) {
    // Выдача поиска ЗАМЕЩАЕТ содержимое папки, а не дополняет его: искали по всему
    // проекту, и показывать рядом папку, к которой запрос отношения не имеет, значило бы
    // выдавать чужие плитки за найденные. Выбор папки при этом не сбрасывается — крестик
    // в поле возвращает ровно туда, где человек был.
    content = (
      <LibrarySearchResults
        results={search.results}
        query={search.query}
        loading={search.loading}
        failed={search.failed}
        tree={tree}
        canvases={canvases}
        flows={flows}
        entities={entities}
        mode={viewMode}
        frameRate={frameRate}
        focusedKey={contentCursorKey}
        onFocusItem={browser.setFocusedKey}
        onOpenItem={browser.handleOpenItem}
        onItemsChange={browser.handleItemsChange}
        onRetry={search.retry}
        onSelect={(next) => {
          search.clear()
          select(next)
        }}
      />
    )
  } else if (selection.section === "shots") {
    const node = selection.node
    if (!node) {
      content = <LibraryNodeEmpty>{t("library.tree.noShots")}</LibraryNodeEmpty>
    } else if (node.level === "shot" || node.level === "scene" || node.level === "episode") {
      const scope =
        node.level === "shot"
          ? { shotId: node.shotId }
          : node.level === "scene"
            ? { sceneId: node.sceneId }
            : { episodeId: node.episodeId }
      const key =
        node.level === "shot"
          ? `shot-${node.shotId}`
          : node.level === "scene"
            ? `scene-${node.sceneId}`
            : `episode-${node.episodeId}`
      content = (
        <LibraryShotFolder key={key} projectId={projectId} scope={scope} frameFormat={frameFormat} {...nodeProps} />
      )
    } else if (node.level === "animatic") {
      content = (
        <LibraryAnimaticNode
          key={`animatic-${node.episodeId}`}
          projectId={projectId}
          episodeId={node.episodeId}
          frameFormat={frameFormat}
          {...nodeProps}
        />
      )
    }
  } else if (selection.section === "canvas") {
    content =
      selection.canvasId != null ? (
        <LibraryCanvasNode
          key={`canvas-${selection.canvasId}`}
          projectId={projectId}
          canvasId={selection.canvasId}
          frameRate={frameRate}
          {...nodeProps}
        />
      ) : (
        <LibraryNodeEmpty>{t("library.tree.noCanvases")}</LibraryNodeEmpty>
      )
  } else if (selection.section === "flow") {
    content = selection.orphans ? (
      <LibraryFlowNode key="flow-orphans" projectId={projectId} orphans frameRate={frameRate} {...nodeProps} />
    ) : selection.flowId != null ? (
      <LibraryFlowNode
        key={`flow-${selection.flowId}`}
        projectId={projectId}
        flowId={selection.flowId}
        frameRate={frameRate}
        {...nodeProps}
      />
    ) : (
      <LibraryNodeEmpty>{t("library.tree.noFlows")}</LibraryNodeEmpty>
    )
  } else if (selection.section === "sketches") {
    content = <LibrarySketchesNode key="sketches" projectId={projectId} {...nodeProps} />
  } else if (selection.section === "uploads") {
    content = (
      <LibraryUploadsNode
        key="uploads"
        projectId={projectId}
        reloadToken={uploadsOptions?.reloadToken}
        canEdit={uploadsOptions?.canEdit}
        onCountChange={uploadsOptions?.onCountChange}
        {...nodeProps}
      />
    )
  } else {
    content =
      selection.entityId != null ? (
        <LibraryEntityFolder
          key={`${selection.section}-${selection.entityId}`}
          projectId={projectId}
          section={selection.section}
          entityId={selection.entityId}
          {...nodeProps}
        />
      ) : (
        <LibraryNodeEmpty>{t(`library.tree.no.${selection.section}`)}</LibraryNodeEmpty>
      )
  }

  const treeNode = (
    <div onFocusCapture={() => browser.setFocusZone("tree")}>
      <LibraryTree
        projectId={projectId}
        tree={tree}
        canvases={canvases}
        flows={flows}
        entities={entities}
        sketches={sketches}
        uploads={uploads}
        activeEpisodeId={activeEpisodeId}
        selection={selection}
        onSelect={select}
        hasFocus={browser.focusZone === "tree"}
        onEnterContent={browser.focusContent}
      />
    </div>
  )

  // Крошки слева, управление справа. Колонки тут не годятся: ширина поиска и фильтра
  // меняется от содержимого (крестик вместо подсказки, другие счётчики), и в центральной
  // колонке этот блок ездил бы по горизонтали при наборе.
  // На узкой панели управление переносится на следующую строку, а не сжимает путь
  // в столбик: у левой группы есть пол, и flex-wrap отдаёт ей всю строку.
  const toolbar = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex max-w-full min-w-[min(100%,12rem)] flex-1 items-center gap-2">
        {toolbarStart}
        <div className="min-w-0 shrink">
          <LibraryBreadcrumbs crumbs={browser.crumbs} />
        </div>
        {crumbsEnd}
      </div>
      <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">
        <LibraryKindFilter items={browser.items} value={browser.kindFilter} onChange={browser.setKindFilter} />
        <LibrarySearchField
          value={search.query}
          onChange={search.setQuery}
          loading={search.loading}
          tooShort={search.tooShort}
          // Только в модалке: на странице раздела фокус в поле мешал бы сразу ходить
          // по дереву стрелками, а модалку открывают, уже зная, что ищут.
          autoFocus={variant === "dialog"}
        />
        {toolbarEnd}
        <LibraryViewToggle mode={viewMode} onChange={setViewMode} />
      </div>
    </div>
  )

  const asset = browser.currentAsset
  const imageUrl = asset?.kind === "image" ? (asset.url ?? null) : null
  const videoUrl = asset?.kind === "video" ? (asset.videoUrl ?? asset.url ?? null) : null
  const audioUrl = asset?.kind === "audio" ? (asset.url ?? null) : null
  const hasMedia = Boolean(imageUrl || videoUrl || audioUrl)
  const assetDescription = useMemo(
    () => libraryAssetDescription(asset, locale, (fps) => t("library.viewer.fps", { fps })),
    [asset, locale, t],
  )

  const viewer = (
    <ImageLightboxDialog
      open={asset != null}
      onOpenChange={(next) => {
        if (!next) browser.closeAsset()
      }}
      url={imageUrl}
      videoUrl={videoUrl}
      audioUrl={audioUrl}
      alt={asset?.title}
      title={asset?.title}
      description={assetDescription}
      downloadUrl={asset?.downloadUrl}
      copyable={asset?.kind === "image" && Boolean(asset.downloadUrl)}
      // В модалке лайтбокс открывается поверх уже вложенного диалога.
      nested={variant === "dialog" ? 2 : true}
      videoSourceId={asset?.sourceId}
      videoDurationMs={asset?.durationMs}
      videoFrameRate={asset?.frameRate}
      videoAspectRatio={asset?.aspectRatio}
      videoTimelineFrameUrls={asset?.timelineFrameUrls}
      videoSourceInMs={asset?.sourceInMs}
      videoSourceOutMs={asset?.sourceOutMs}
      onPrev={() => browser.stepAsset(-1)}
      onNext={() => browser.stepAsset(1)}
      hasPrev={browser.hasPrev}
      hasNext={browser.hasNext}
      prevLabel={t("library.viewer.prev")}
      nextLabel={t("library.viewer.next")}
      emptyMessage={asset != null && !hasMedia ? t("library.viewer.noPreview") : null}
      onOpenAutoFocus={(event) => {
        // Radix фокусирует первую кнопку — крестик: он подсвечивается и ловит Enter.
        // Держим фокус на диалоге: Escape закрывает, стрелки листают.
        event.preventDefault()
        const content = event.currentTarget
        if (content instanceof HTMLElement) content.focus({ preventScroll: true })
      }}
      onCloseAutoFocus={(event) => {
        // Radix вернул бы фокус на кнопку, с которой открывали; после перелистывания это чужая плитка.
        event.preventDefault()
        browser.restoreContentFocus()
      }}
    />
  )

  // Шасси одно на оба варианта: панель крошек с чертой, дерево фиксированной ширины
  // за разделителем, содержимое со своей прокруткой.
  //
  // Раздел не заворачивается в карточку: у страницы уже есть своя граница, и скруглённая
  // рамка поверх неё читалась как задвоенная. Вместо неё — та же поверхность фоном во всю
  // область; она же держит совпадение с липкими заголовками групп и дерева.
  const body = (
    <div
      ref={rootRef}
      className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", variant === "page" && "bg-ui-bg-elevated")}
    >
      <div className="shrink-0 border-b border-ui-border px-3 py-2">{toolbar}</div>
      <div
        className={cn(
          "grid min-h-0 flex-1 overflow-hidden",
          treeHidden ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-[15rem_minmax(0,1fr)]",
        )}
      >
        {treeHidden ? null : (
          <div className="it2tv-scrollbar hidden min-h-0 overflow-y-auto border-r border-ui-border p-2 sm:block">
            {treeNode}
          </div>
        )}
        <div
          ref={contentRef}
          className="it2tv-scrollbar min-h-0 overflow-y-auto"
          onKeyDown={browser.handleContentKeyDown}
          onFocusCapture={() => browser.setFocusZone("content")}
        >
          {content}
        </div>
      </div>
    </div>
  )

  return (
    <LibraryPickProvider value={pick}>
      <LibraryKindFilterProvider value={browser.kindFilter}>
        <LibrarySortProvider sort={browser.sort} onToggle={browser.toggleSort}>
          {body}
          {viewer}
        </LibrarySortProvider>
      </LibraryKindFilterProvider>
    </LibraryPickProvider>
  )
}

function libraryAssetDescription(
  asset: LibraryAsset | null,
  locale: string,
  fpsLabel: (fps: number) => string,
): string | null {
  if (!asset) return null
  const parts: string[] = []
  if (asset.createdAt) {
    try {
      parts.push(new Date(asset.createdAt).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" }))
    } catch {
      // Невалидная дата не должна ронять просмотр.
    }
  }
  if (asset.kind === "video") {
    const durationMs = asset.durationMs ?? null
    const sourceInMs = asset.sourceInMs ?? null
    const sourceOutMs = asset.sourceOutMs ?? null
    // У подрезанного клипа показываем длину диапазона, а не всего файла-источника.
    const shownMs =
      sourceInMs != null && sourceOutMs != null && sourceOutMs > sourceInMs ? sourceOutMs - sourceInMs : durationMs
    if (shownMs) parts.push(formatMs(shownMs))
    const fps = asset.frameRate != null && asset.frameRate > 0 ? asset.frameRate : DEFAULT_VIDEO_OUTPUT_FPS
    parts.push(fpsLabel(Math.round(fps)))
  }
  if (asset.kind === "audio" && asset.durationMs) parts.push(formatMs(asset.durationMs))
  return parts.length > 0 ? parts.join(" · ") : null
}
